/**
 * Component: EventReportsTab
 * Event reports computed from the participant list:
 *   - by district (registered, verified, paid, attended, collected)
 *   - by pricing rule (count, collected)
 *   - mandatory participants who did not attend (with contact data for follow-up)
 *   - charts: registrations over time and by district; attendance per session
 * Each table exports to CSV.
 */

import { useMemo } from 'react';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatPrice } from '@/models/event';
import { BarChartAnimated } from '@/components/ui/bar-chart-animated';
import { Skeleton } from '@/components/ui/skeleton';
import { useEventParticipants } from '../hooks/useEventParticipants';
import { SessionAttendanceCard } from '../checkin/components/SessionAttendanceCard';
import { RegistrationsTimelineChart } from './RegistrationsTimelineChart';
import { registrationsByDay } from '../utils/reportCharts';
import { csvFileName, downloadCsv } from '../utils/csv';
import type { ParticipantWithDetails } from '@/Services/eventParticipants.service';

interface EventReportsTabProps {
  eventId: string;
  eventName: string;
}

interface Row {
  key: string;
  label: string;
  registered: number;
  verified: number;
  paid: number;
  attended: number;
  collected: number;
}

function emptyRow(key: string, label: string): Row {
  return { key, label, registered: 0, verified: 0, paid: 0, attended: 0, collected: 0 };
}

function add(row: Row, p: ParticipantWithDetails) {
  row.registered++;
  if (p.registration_status === 'VERIFIED') row.verified++;
  if (p.payment_status === 'PAID') row.paid++;
  if (p.attendance_status === 'ATTENDED') row.attended++;
  row.collected += Number(p.payment_amount ?? 0);
}

function sum(rows: Row[]): Row {
  const total = emptyRow('total', 'Total');
  rows.forEach((r) => {
    total.registered += r.registered;
    total.verified += r.verified;
    total.paid += r.paid;
    total.attended += r.attended;
    total.collected += r.collected;
  });
  return total;
}

const name = (p: ParticipantWithDetails) => p.minister?.full_name ?? p.guest?.full_name ?? '';
const phone = (p: ParticipantWithDetails) => p.minister?.cell_phone ?? p.guest?.phone ?? '';

export function EventReportsTab({ eventId, eventName }: EventReportsTabProps) {
  const { data: all = [], isLoading } = useEventParticipants(eventId);
  const participants = useMemo(() => all.filter((p) => p.registration_status !== 'REJECTED'), [all]);

  const byDistrict = useMemo(() => {
    const map = new Map<string, Row>();
    participants.forEach((p) => {
      const label = p.minister?.district?.name ?? (p.guest?.is_delegate ? 'Delegados' : 'Invitados / sin distrito');
      const row = map.get(label) ?? emptyRow(label, label);
      add(row, p);
      map.set(label, row);
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [participants]);

  const byRule = useMemo(() => {
    const map = new Map<string, Row>();
    participants.forEach((p) => {
      const label = p.pricing_rule?.name ?? 'Precio base';
      const row = map.get(label) ?? emptyRow(label, label);
      add(row, p);
      map.set(label, row);
    });
    return [...map.values()].sort((a, b) => b.registered - a.registered);
  }, [participants]);

  const timeline = useMemo(() => registrationsByDay(participants.map((p) => p.registration_date)), [participants]);

  const mandatoryAbsent = useMemo(
    () =>
      participants
        .filter((p) => p.is_mandatory && p.attendance_status !== 'ATTENDED')
        .sort((a, b) => name(a).localeCompare(name(b), 'es')),
    [participants],
  );

  const exportRows = (prefix: string, rows: Row[], firstHeader: string) =>
    downloadCsv(
      csvFileName(prefix, eventName),
      [firstHeader, 'Inscritos', 'Verificados', 'Pagados', 'Asistieron', 'Recaudado (Bs)'],
      [...rows, sum(rows)].map((r) => [r.label, r.registered, r.verified, r.paid, r.attended, r.collected.toFixed(2)]),
    );

  if (isLoading) {
    return (
      <div className="space-y-6" aria-busy="true" aria-label="Cargando reportes">
        <div className="grid gap-6 lg:grid-cols-2">
          <Skeleton className="h-[360px]" />
          <Skeleton className="h-[360px]" />
        </div>
        <Skeleton className="h-48" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Inscripciones en el tiempo</CardTitle>
          </CardHeader>
          <CardContent>
            <RegistrationsTimelineChart points={timeline} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Inscritos por distrito</CardTitle>
          </CardHeader>
          <CardContent>
            <BarChartAnimated data={byDistrict.map((r) => ({ name: r.label, value: r.registered }))} height={300} />
          </CardContent>
        </Card>
      </div>

      <SessionAttendanceCard eventId={eventId} />

      <ReportTable
        title="Por distrito"
        firstHeader="Distrito"
        rows={byDistrict}
        onExport={() => exportRows('reporte_distritos', byDistrict, 'Distrito')}
      />
      <ReportTable
        title="Por regla de precio"
        firstHeader="Regla"
        rows={byRule}
        onExport={() => exportRows('reporte_reglas', byRule, 'Regla')}
      />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Obligatorios sin asistencia ({mandatoryAbsent.length})</CardTitle>
          <Button
            variant="outline"
            size="sm"
            disabled={mandatoryAbsent.length === 0}
            onClick={() =>
              downloadCsv(
                csvFileName('obligatorios_ausentes', eventName),
                ['Nombre', 'Código ADB', 'Cargo', 'Distrito', 'Región', 'Celular'],
                mandatoryAbsent.map((p) => [
                  name(p),
                  p.minister?.adb_code ?? '',
                  p.authority_position ? `${p.authority_position} - ${p.authority_entity}` : '',
                  p.minister?.district?.name ?? '',
                  p.minister?.region?.name ?? '',
                  phone(p),
                ]),
              )
            }
          >
            <Download className="mr-2 size-4" /> CSV
          </Button>
        </CardHeader>
        <CardContent>
          {mandatoryAbsent.length === 0 ? (
            <p className="text-sm text-muted-foreground">Todos los participantes obligatorios registraron asistencia.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Cargo</TableHead>
                    <TableHead>Distrito</TableHead>
                    <TableHead>Región</TableHead>
                    <TableHead>Celular</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mandatoryAbsent.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">{name(p)}</TableCell>
                      <TableCell>{p.authority_position ? `${p.authority_position} - ${p.authority_entity}` : '—'}</TableCell>
                      <TableCell>{p.minister?.district?.name ?? '—'}</TableCell>
                      <TableCell>{p.minister?.region?.name ?? '—'}</TableCell>
                      <TableCell>{phone(p) || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ReportTable({
  title,
  firstHeader,
  rows,
  onExport,
}: {
  title: string;
  firstHeader: string;
  rows: Row[];
  onExport: () => void;
}) {
  const total = sum(rows);
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{title}</CardTitle>
        <Button variant="outline" size="sm" onClick={onExport} disabled={rows.length === 0}>
          <Download className="mr-2 size-4" /> CSV
        </Button>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{firstHeader}</TableHead>
                <TableHead className="text-right">Inscritos</TableHead>
                <TableHead className="text-right">Verificados</TableHead>
                <TableHead className="text-right">Pagados</TableHead>
                <TableHead className="text-right">Asistieron</TableHead>
                <TableHead className="text-right">Recaudado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.key}>
                  <TableCell className="font-medium">{r.label}</TableCell>
                  <TableCell className="text-right">{r.registered}</TableCell>
                  <TableCell className="text-right">{r.verified}</TableCell>
                  <TableCell className="text-right">{r.paid}</TableCell>
                  <TableCell className="text-right">{r.attended}</TableCell>
                  <TableCell className="text-right">{formatPrice(r.collected)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell>Total</TableCell>
                <TableCell className="text-right">{total.registered}</TableCell>
                <TableCell className="text-right">{total.verified}</TableCell>
                <TableCell className="text-right">{total.paid}</TableCell>
                <TableCell className="text-right">{total.attended}</TableCell>
                <TableCell className="text-right">{formatPrice(total.collected)}</TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
